import {defineStore} from 'pinia';
import {useMenuStore, useOrdersStore, useRoomsStore, useUserStore, useNativeStore} from "@/console-new/stores";
import {useSunmiStore} from "@/console-sun/stores/sunmi.store";

import API from '@/shared/helpers/API';
import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {formatDateTime, formatDateOnly} from "@/shared/helpers/dates";
import {getRandomAlphanumericString} from "@/shared/helpers/strings";
import {getNative, isAndroid} from "@/shared/helpers/webapp";

const BROADCAST_CHANNEL_NAME = 'qromo-counter-display';
let _broadcastChannel = null;
let _broadcastDebounce = null;

const _displayStateKeys = [
	'cartItems', 'fakeItems', 'subtotalCosts', 'tip',
	'coverCharge', 'coverNum', 'deliveryCosts', 'takeawayCosts',
	'selectedMode', 'selectedTable', 'orderNotes', 'editOrder',
	'selectedCourse', 'maxCourse',
];

export const useCounterStore = defineStore('counter', {
	state: () => ({
		fakeItems: [],
		cartItems: [],
		compositeProducts: [],
		currComposite: null,
		loadingComposite: false,
		customerNotEditable: false,
		noPayLater: false,
		creditTopUpMode: false,
		subtotalCosts: 0,
		tip: 0,
		coverCharge: 0,
		coverNum: 0,
		deliveryCosts: 0,
		takeawayCosts: 0,
		editingItem: null,
		paymentMethod: null,
		setInvoiceAsPaid: true,
		selectedCustomer: null,
		loadingSelectedCustomer: false,
		pendingConsumerLink: null,
		registryEditingCustomer: null,
		invoiceAdditionalData: {
			purchase_order: null,
			payment_due_date: null,
		},
		selectedTable: null,
		selectedMode: "counter",
		deliveryAddress: null,
		customerName: null,
		customerPhone: null,
		customerEmail: null,
		notInPlaceDate: null,
		orderNotes: null,
		search: null,
		mostSold: null,
		editOrder: null,
		selectedCourse: 1,
		maxCourse: 1,
		tablesActive: false,
		paymentActive: false,
		quickPayPending: false,
		quickPaySnapshot: null,
		shortcuts: null,
		externalPaymentIntentData: null,
		externalPaymentIntentWrongAppId: false,
		qrScannerActive: false
	}),
	getters: {
		runningCourse: state => {
			if (state.editOrder) {
				return state.editOrder.course ? state.editOrder.course : null;
			} else {
				return null;
			}
		},
		cartTotal: state => {
			let cartItems = state.cartItems.reduce((acc, curr) => {
				return acc + curr.quantity * (curr.price + curr.products.reduce((acc2, curr) => {
					return acc2 + curr.price * curr.quantity;
				}, 0));
			}, 0);
			let fakeItems = state.fakeItems.reduce((acc, curr) => {
				return acc + curr.price * curr.quantity;
			}, 0);
			return cartItems + fakeItems;
		},
		additionalCosts: state => {
			return (state.coverCharge * state.coverNum) + state.deliveryCosts + state.takeawayCosts + state.subtotalCosts + state.tip;
		},
		totalNoCover: state => {
			return state.cartTotal + state.deliveryCosts + state.takeawayCosts + state.subtotalCosts + state.tip;
		},
		totalPreSubtotal: state => {
			return state.cartTotal + (state.coverCharge * state.coverNum) + state.deliveryCosts + state.takeawayCosts + state.tip;
		},
		grandTotal: state => {
			return state.cartTotal + state.additionalCosts;
		},
		cartItemsCount: state => {
			let cartItems = state.cartItems.reduce((acc, curr) => {
				return acc + curr.quantity + curr.products.reduce((acc, curr) => {
					return acc + curr.quantity;
				}, 0);
			}, 0);
			let fakeItems = state.fakeItems.reduce((acc, curr) => {
				return acc + curr.quantity;
			}, 0);
			return cartItems + fakeItems;
		},
		cartQuantitiesById: state => {
			const m = Object.create(null);
			for (const ci of state.cartItems) {
				m[ci.menu_item_id] = (m[ci.menu_item_id] || 0) + ci.quantity;
			}
			return m;
		},
		modeTitle: state => {
			const languageStore = useLanguageStore();
			if (state.selectedMode === "counter") {
				return languageStore.getString("direct_sale");
			} else if (state.selectedMode === "qr") {
				return state.selectedTable ? state.selectedTable.table_name : "-";
			} else if (state.selectedMode === "delivery") {
				return languageStore.getString("delivery");
			} else if (state.selectedMode === "takeaway") {
				const userStore = useUserStore();
				return userStore.takeawayWord;
			} else {
				return "-";
			}
		},
		withCourses: state => {
			const userStore = useUserStore();
			if (!userStore.currBusiness || userStore.currBusiness?.active_plan === 'free' || !userStore.currBusiness.counter_courses) {
				return false;
			}
			let noCoursesModes = ["delivery", "takeaway"];
			if (state.editOrder) {
				return !noCoursesModes.includes(state.editOrder.order_mode);
			} else {
				return !noCoursesModes.includes(state.selectedMode);
			}
		}
	},
	actions: {
		removeUnusedCourses(greaterThan) {
			for (let i = this.maxCourse; i > greaterThan; i--) {
				let index = this.cartItems.find(item => item.course === i);
				if (!index) {
					this.maxCourse--;
				} else {
					break;
				}
			}
			if (this.selectedCourse > this.maxCourse) {
				this.selectedCourse = this.maxCourse;
			}
		},
		startEditOrder(order) {
			this.editOrder = order;
			let firstCourseWithItems = null;
			let maxCourse = 1;
			this.cartItems = order.items.map(item => {
				if (!firstCourseWithItems || item.course < firstCourseWithItems) {
					firstCourseWithItems = item.course;
				}
				if (item.course > maxCourse) {
					maxCourse = item.course;
				}
				return {
					identifier: getRandomAlphanumericString(10),
					order_item_id: item.order_item_id,
					menu_item_id: Number(item.item_id),
					name: item.name,
					price: item.price,
					menu_item: item.menu_item,
					quantity: item.quantity,
					note: item.note,
					products: item.products.map(product => {
						return {
							menu_item_list_product_id: product.menu_item_list_product_id,
							product_name: product.product_name,
							price: product.price,
							quantity: product.quantity,
						}
					}),
					course: item.course,
				}
			});
			this.subtotalCosts = order.subtotal_delta;
			this.tip = order.tip;
			this.orderNotes = order.notes;
			this.coverNum = order.cover_charge_num ? order.cover_charge_num : 0;
			this.coverCharge = order.cover_charge_value ? order.cover_charge_value : 0;
			this.deliveryCosts = order.delivery_costs_value ? order.delivery_costs_value : 0;
			this.takeawayCosts = order.takeaway_costs_value ? order.takeaway_costs_value : 0;
			this.selectedCourse = firstCourseWithItems ? firstCourseWithItems : 1;
			this.maxCourse = maxCourse;
			if (this.runningCourse) {
				this.selectedCourse = this.runningCourse;
				if (this.runningCourse > this.maxCourse) {
					this.maxCourse = this.runningCourse;
				}
			}
		},
		finishEditOrder() {
			this.editOrder = null;
			this.clearCart();
		},
		clearCart() {
			this.cartItems = [];
			this.fakeItems = [];
			this.compositeProducts = [];
			this.subtotalCosts = 0;
			this.tip = 0;
			this.paymentMethod = null;
			this.selectedMode = "counter";
			this.selectedTable = null;
			this.coverCharge = 0;
			this.coverNum = 0;
			this.deliveryCosts = 0;
			this.takeawayCosts = 0;
			this.selectedCourse = 1;
			this.maxCourse = 1;
			this.deliveryAddress = null;
			this.customerName = null;
			this.customerPhone = null;
			this.customerEmail = null;
			this.notInPlaceDate = null;
			this.orderNotes = null;
		},
		addProduct(product) {
			const id = product.menu_item_list_product_id;
			const index = this.compositeProducts.findIndex(item => item.menu_item_list_product_id === id);
			if (index === -1) {
				let prodName = product.product_name;
				let prodPrice = product.product_price;
				if(!product.meta_product && product.menu_item_linked_id && (product.linked_price || product.linked_name)) {
					let linkedProduct = useMenuStore().modItemFromId(product.menu_item_linked_id);
					prodName = product.linked_name && linkedProduct ? linkedProduct.name : prodName;
					prodPrice = product.linked_price && linkedProduct ? linkedProduct.price : prodPrice;
				}

				this.compositeProducts.push({
					menu_item_list_product_id: product.menu_item_list_product_id,
					product_name: prodName,
					price: prodPrice,
					quantity: 1,
					order_number: product.order_number,
					list_order_number: product.list_order_number,
				});
			} else {
				this.compositeProducts[index].quantity++;
			}
		},
		removeProduct(product) {
			const id = product.menu_item_list_product_id;
			const index = this.compositeProducts.findIndex(item => item.menu_item_list_product_id === id);
			if (index !== -1) {
				if (this.compositeProducts[index].quantity > 1) {
					this.compositeProducts[index].quantity--;
				} else {
					this.compositeProducts.splice(index, 1);
				}
			}
		},
		getProductQuantity(product) {
			const id = product.menu_item_list_product_id;
			const index = this.compositeProducts.findIndex(item => item.menu_item_list_product_id === id);
			if (index !== -1) {
				return this.compositeProducts[index].quantity;
			}
			return 0;
		},
		getCartItemQuantity(item) {
			return this.cartQuantitiesById[item.menu_item_id] || 0;
		},
		addCartItem(item) {
			let userStore = useUserStore();

			if(
				(!this.editOrder && !userStore.checkOrderCreationAllowed) ||
				(this.editOrder && !userStore.checkOrderEditAllowed)
			) {
				let notificationStore = useNotificationStore();
				notificationStore.showNotification(this.editOrder ? "permission-denied-order-edit" : "permission-denied-order-creation");
				return;
			}

			if (item.composite) {
				this.currComposite = item;
				if (!item.compositeLoaded) {
					this.loadingComposite = true;
					let menuStore = useMenuStore();
					menuStore.getAllCompositeInfo(item.menu_item_id, (result) => {
						if (!result) {
							let notificationStore = useNotificationStore();
							notificationStore.showNotification('composed-no-items');
						}
						this.loadingComposite = false;
					});
				}
				return;
			}
			const id = Number(item.menu_item_id);
			const index = this.cartItems.findIndex(item => item.menu_item_id === id && item.course === this.selectedCourse && item.note === null);
			if (index === -1) {
				this.cartItems.push({
					identifier: getRandomAlphanumericString(10),
					menu_item_id: id,
					name: item.name,
					price: item.price,
					menu_item: {
						name: item.name,
						picture: item.picture,
					},
					quantity: 1,
					note: null,
					products: [],
					course: this.selectedCourse,
					natura_iva: item.natura_iva
				});
			} else {
				this.cartItems[index].quantity++;
			}
		},
		splitItem(item) {
			const id = item.identifier;
			const index = this.cartItems.findIndex(item => item.identifier === id);
			if (index !== -1 && this.cartItems[index].quantity > 1) {
				let cloneItem = {
					...this.cartItems[index],
					identifier: getRandomAlphanumericString(10),
					quantity: 1,
				}
				delete cloneItem.order_item_id;
				this.cartItems[index].quantity--;
				this.cartItems.push(cloneItem);
			}
		},
		addCartFakeItem(value) {

			let randomId = getRandomAlphanumericString(10);
			this.fakeItems.push({
				identifier: randomId,
				price: value,
				quantity: 1,
			});
			return randomId;

		},
		startQuickPay(value) {
			// Snapshot the current cart so it can be restored after the standalone payment.
			// When the cart is otherwise empty there is nothing to preserve, so store an
			// empty snapshot: restore then simply resets the cart back to empty.
			this.quickPaySnapshot = this.cartItemsCount > 0 ? {
				cartItems: this.cartItems,
				fakeItems: this.fakeItems,
				compositeProducts: this.compositeProducts,
				subtotalCosts: this.subtotalCosts,
				tip: this.tip,
				selectedMode: this.selectedMode,
				selectedTable: this.selectedTable,
				coverCharge: this.coverCharge,
				coverNum: this.coverNum,
				deliveryCosts: this.deliveryCosts,
				takeawayCosts: this.takeawayCosts,
				selectedCourse: this.selectedCourse,
				maxCourse: this.maxCourse,
				deliveryAddress: this.deliveryAddress,
				customerName: this.customerName,
				customerPhone: this.customerPhone,
				customerEmail: this.customerEmail,
				notInPlaceDate: this.notInPlaceDate,
				orderNotes: this.orderNotes,
				selectedCustomer: this.selectedCustomer,
			} : {};
			this.clearCart();
			this.addCartFakeItem(value);
			this.quickPayPending = true;
		},
		restoreQuickPaySnapshot() {
			if (!this.quickPaySnapshot) {
				return;
			}
			let snapshot = this.quickPaySnapshot;
			this.quickPaySnapshot = null;
			this.clearCart();
			Object.assign(this, snapshot);
		},
		removeCartItem(item, quantity = 1) {
			let index = -1;
			if (item.identifier) {
				const id = item.identifier;
				index = this.cartItems.findIndex(item => item.identifier === id);
			} else {
				const id = item.menu_item_id;
				index = this.cartItems.findIndex(item => item.menu_item_id === id);
			}
			if (index !== -1) {
				if (this.cartItems[index].quantity > quantity) {
					this.cartItems[index].quantity -= quantity;
				} else {
					this.cartItems.splice(index, 1);

					if (this.withCourses) {
						this.removeUnusedCourses(1);
					}
				}
			}
		},
		changeQuantityCartFakeItem(item, quantity = 1) {
			const id = item.identifier;
			const index = this.fakeItems.findIndex(item => item.identifier === id);
			if (index !== -1) {
				if (quantity <= 0) {
					this.fakeItems.splice(index, 1);
				} else {
					this.fakeItems[index].quantity = quantity;
				}
			}
		},
		changePriceCartFakeItem(item, price) {
			const id = item.identifier;
			const index = this.fakeItems.findIndex(item => item.identifier === id);
			if (index !== -1) {
				this.fakeItems[index].price = price;
			}
		},
		addCompositeItem() {
			if (this.currComposite) {
				//order products before adding
				this.compositeProducts = this.compositeProducts.sort((a, b) => {
					if(a.hasOwnProperty("list_order_number") && b.hasOwnProperty("list_order_number")) {
						if(a.list_order_number < b.list_order_number) {
							return -1;
						} else if(a.list_order_number > b.list_order_number) {
							return 1;
						} else {
							return a.order_number < b.order_number ? -1 : 1;
						}
					}
				});
				this.cartItems.push({
					identifier: getRandomAlphanumericString(10),
					menu_item_id: Number(this.currComposite.menu_item_id),
					name: this.currComposite.name,
					price: this.currComposite.price,
					menu_item: {
						name: this.currComposite.name,
						picture: this.currComposite.picture,
					},
					quantity: 1,
					note: null,
					products: this.compositeProducts,
					course: this.selectedCourse,
					natura_iva: this.currComposite.natura_iva
				});
				this.currComposite = null;
				this.compositeProducts = [];
			}
		},
		getMostSoldProducts() {
			let userStore = useUserStore();
			let mostSoldStorage = localStorage.getItem("most-sold");
			if (localStorage.getItem("most-sold-business-id") && (parseInt(localStorage.getItem("most-sold-business-id")) === parseInt(userStore.currBusinessId)) && mostSoldStorage) {
				this.mostSold = JSON.parse(mostSoldStorage);
			} else {
				localStorage.removeItem("most-sold");
				localStorage.removeItem("most-sold-business-id");
				API.init().getMostSoldProducts(userStore.currBusinessId, res => {
					if (res && res["result"] && res["result"] === "OK") {

						let mostSoldItems = {};
						res.most_sold.forEach((item) => {
							mostSoldItems[item.item_id] = parseInt(item.total);
						});

						this.mostSold = mostSoldItems;
						localStorage.setItem("most-sold", JSON.stringify(mostSoldItems));
						localStorage.setItem("most-sold-business-id", parseInt(userStore.currBusinessId));
					} else {
						console.warn("getMostSoldProducts failed");
					}
				});
			}
		},
		updateMostSoldProducts(items) {
			let newMostSold = [];
			let localMostSold = localStorage.getItem("most-sold");
			if (localMostSold) {
				newMostSold = JSON.parse(localMostSold);
				items.forEach(item => {
					if (newMostSold.hasOwnProperty(item.menu_item_id)) {
						newMostSold[item.menu_item_id] += item.quantity;
					} else {
						newMostSold[item.menu_item_id] = item.quantity;
					}
				});
			} else {
				items.forEach(item => {
					newMostSold[item.menu_item_id] = item.quantity;
				});
			}
			localStorage.setItem("most-sold", JSON.stringify(newMostSold));
			this.mostSold = newMostSold;
		},
		createOrder(amountPaidPartial, discountOnTotal, appliedCoupons, itemsSplit, dutchPeople, requestingPayment, onlyTotalItem, callback = null) {
			if (this.selectedMode === "delivery" && this.deliveryAddress) {
				if (this.deliveryAddress.hasOwnProperty("address_id")) {
					this.createOrderContinue(this.deliveryAddress.address_id, amountPaidPartial, discountOnTotal, appliedCoupons, itemsSplit, dutchPeople, requestingPayment, onlyTotalItem, callback);
				} else {
					this.addAddress((addressId) => {
						if (addressId) {
							this.createOrderContinue(addressId, amountPaidPartial, discountOnTotal, appliedCoupons, itemsSplit, dutchPeople, requestingPayment, onlyTotalItem, callback);
						} else {
							callback && callback("error", null);
						}
					});
				}
			} else {
				this.createOrderContinue(null, amountPaidPartial, discountOnTotal, appliedCoupons, itemsSplit, dutchPeople, requestingPayment, onlyTotalItem, callback);
			}

		},
		createOrderContinue(addressId, amountPaidPartial, discountOnTotal, appliedCoupons, itemsSplit, dutchPeople, requestingPayment, onlyTotalItem, callback = null) {

			let userStore = useUserStore();
			let roomsStore = useRoomsStore();
			let items = this.cartItems.map(item => {
				let redeemed = 0;
				if (itemsSplit) {
					let splitItem = itemsSplit.find(split => split.id === item.identifier);
					if (splitItem) {
						redeemed = splitItem.quantity;
					}
				}
				return {
					menu_item_id: item.menu_item_id,
					quantity: item.quantity,
					price: item.price,
					notes: item.note,
					redeemed: redeemed,
					products: item.products.map(product => {
						return {
							menu_item_list_product_id: product.menu_item_list_product_id,
							quantity: product.quantity,
							product_price: product.price,
							nested: false,
						}
					}),
					course: this.withCourses ? item.course : 1,
					natura_iva: item.natura_iva ?? null
				}
			});
			let paymentMethod = this.paymentMethod ? this.paymentMethod : "cash";
			if (requestingPayment) {
				paymentMethod = requestingPayment;
			}
			let paid = !!this.paymentMethod && amountPaidPartial === null && !requestingPayment;
			let roomTableId = this.selectedMode === "qr" && this.selectedTable ? this.selectedTable.room_table_id : null;
			let deliveryAddressId = this.selectedMode === "delivery" ? addressId : null;
			let isNotInPlace = this.selectedMode === "delivery" || this.selectedMode === "takeaway";
			let customerName = isNotInPlace ? this.customerName : null;
			let roomEnabledSms = false;
			if (this.selectedMode === "qr" && this.selectedTable?.room_id) {
				let currRoom = roomsStore.roomsIds[this.selectedTable.room_id];
				roomEnabledSms = !!currRoom?.enabled_sms;
			}
			let needsPhone = isNotInPlace || (this.selectedMode === 'qr' && roomEnabledSms);
			let customerPhone = needsPhone ? this.customerPhone : null;
			if(!customerPhone && needsPhone && !!this.selectedCustomer?.phone) {
				customerPhone = this.selectedCustomer?.phone
			}
			let customerEmail = isNotInPlace ? this.customerEmail : null;
			let notInPlaceDate = isNotInPlace ? formatDateTime(this.notInPlaceDate) : null;
			let fakeItemsTotal = this.fakeItems.reduce((acc, curr) => {
				return acc + curr.price * curr.quantity;
			}, 0);
			let ordersStore = useOrdersStore();
			let status = paid && userStore.currBusiness.autocomplete_payed_counter && this.selectedMode === "counter" ? ordersStore.STATUS.completed : ordersStore.STATUS.preparing;
			if (requestingPayment) {
				status = ordersStore.STATUS.unconfirmed;
			}
			let invoiceData = null;
			if (this.selectedCustomer) {
				let invoiceDataObj = {
					customer_id: this.selectedCustomer.customer_id,
					customer_type: this.selectedCustomer.type,
					invoice_paid: true,
				};
				if (!customerName && (isNotInPlace || !amountPaidPartial)) {
					customerName = this.selectedCustomer?.name || null;
				}
				if(!!paymentMethod && ["wire", "check"].includes(paymentMethod)) {
					invoiceDataObj.invoice_paid = this.setInvoiceAsPaid;
				}
				if(this.invoiceAdditionalData?.purchase_order) {
					invoiceDataObj.purchase_order = this.invoiceAdditionalData.purchase_order;
				}
				if(!invoiceDataObj.invoice_paid && this.invoiceAdditionalData?.payment_due_date) {
					invoiceDataObj.payment_due_date = formatDateOnly(this.invoiceAdditionalData.payment_due_date);
				}
				invoiceData = JSON.stringify(invoiceDataObj);
			}
			let coverPaid = null;
			if (this.coverNum > 0 && itemsSplit) {
				itemsSplit.forEach(item => {
					if(item.order_item_id && item.order_item_id === "cover") {
						coverPaid = item.quantity;
					}
				});
				if(coverPaid && coverPaid > this.coverNum) {
					coverPaid = this.coverNum;
				}
			}
			const sunmiStore = useSunmiStore();
			const shouldExpectFiscalPrint =
				(paid || !!requestingPayment || amountPaidPartial !== null) &&
					sunmiStore.canLocalPrint && useNativeStore().allowLocalReceiptPrint &&
					userStore.currBusiness?.apiscontrino_id;

			if(shouldExpectFiscalPrint && useNativeStore().allowLocalReceiptPrint) {
				sunmiStore.expectingFiscalPrint = true;
			}

			const idempotencyKey = getRandomAlphanumericString(16);

			API.init().addOrderCounter(userStore.currBusinessId, this.selectedMode, JSON.stringify(items), this.subtotalCosts + fakeItemsTotal, this.orderNotes, customerName, customerEmail, customerPhone, roomTableId, deliveryAddressId, this.coverNum, this.coverCharge, coverPaid, this.takeawayCosts, this.deliveryCosts, JSON.stringify(dutchPeople), status, paymentMethod, paid, notInPlaceDate, amountPaidPartial, discountOnTotal, appliedCoupons, invoiceData, userStore.unlockUserId, onlyTotalItem, idempotencyKey, r => {
				if (r && r.result) {
					if (r.result === "OK" && r.order_id) {

						if(userStore.currSubscription === 'free' && r.weekly_cash_transactions) {
							useUserStore().currBusiness.weekly_cash_transactions = Number(r?.weekly_cash_transactions || 0);
						}

						if (!requestingPayment) {

							this.selectedCustomer = null;
							this.clearCart();
							this.updateMostSoldProducts(items);

						}
						callback && callback("success", r.order_id);
					} else if (r.details) {
                        let returningObj = r.details;
						if(r.details === 'cash_limit_exceeded') {
							useUserStore().currBusiness.weekly_cash_transactions = Number(r?.weekly_cash_transactions || 0);
						} else if(r.details === 'no_credit_available' && r.customer_id && r.customer_type) {
							returningObj = {
								details: 'no_credit_available',
								customer_id: r.customer_id,
								customer_type: r.customer_type,
							}
						}
						callback && callback(returningObj, null);
					} else {
						callback && callback("error", null);
					}
				} else {
					callback && callback("error", null);
				}
			});

		},
		performEditOrder(callback = null, noPrint = false) {

			if (!this.editOrder) {
				callback && callback(false);
				return;
			}

			let userStore = useUserStore();
			let items = this.cartItems.map(item => {
				return {
					order_item_id: item.order_item_id ? item.order_item_id : null,
					menu_item_id: item.menu_item_id,
					quantity: item.quantity,
					price: item.price,
					note: item.note,
					notes: item.note,
					name: item.menu_item ? item.menu_item.name : null,
					products: item.products.map(product => {
						return {
							menu_item_list_product_id: product.menu_item_list_product_id,
							order_item_id: product.order_item_id,
							quantity: product.quantity,
							product_price: product.price,
							price: product.price,
							product_name: product.product_name,
							nested: false,
						}
					}),
					course: this.withCourses ? item.course : 1,
				}
			});
			let fakeItemsTotal = this.fakeItems.reduce((acc, curr) => {
				return acc + curr.price * curr.quantity;
			}, 0);
			API.init().editOrder(userStore.currBusinessId, this.editOrder.order_id, this.editOrder.order_mode, this.editOrder.room_table_id, JSON.stringify(items), this.subtotalCosts + fakeItemsTotal, this.orderNotes, this.coverNum, this.coverCharge, this.takeawayCosts, this.deliveryCosts, noPrint, r => {
				const notificationStore = useNotificationStore();
				if (r && r.result && r.result === "OK") {
					callback && callback(true);
				} else {
					callback && callback(false);
					let errDetails = r && r.details ? r.details : '';
					let notificationName = errDetails === 'order_paid' ? 'edit-order-error-paid' : 'generic-fail';
					notificationName = errDetails === 'order_partially_paid' ? 'edit-order-error-partially-paid' : notificationName;
					notificationStore.showNotification(notificationName);
				}
			});


		},
		addAddress(callback) {
			if (!this.deliveryAddress) {
				callback(null);
				return;
			}
			const userStore = useUserStore();
			API.init().addNewAddress(userStore.currBusinessId, this.deliveryAddress.address, this.deliveryAddress.lat, this.deliveryAddress.lng, JSON.stringify(this.deliveryAddress.data), "order", response => {
				if (response && response.result === "OK" && response.address_id) {
					callback(response.address_id);
				} else {
					callback(null);
				}
			});
		},
		loadShortcuts(callback = null) {
			let userStore = useUserStore();

			if(!userStore.currBusinessId) { callback && callback([]); return; }
			if(this.shortcuts !== null) { callback && callback(this.shortcuts); return; }

			API.init().getBusinessShortcuts(userStore.currBusinessId, (res) => {
				if(res && res.result && res.result === "OK") {
					let normShortcuts = [];
					res.shortcuts.forEach((shortcut) => {
						let normShortcut = this.normalizeShortcut(shortcut);
						normShortcuts.push(normShortcut);
					});
					this.shortcuts = normShortcuts;

					callback && callback(this.shortcuts);
				} else {
					callback && callback([]);
				}
			})
		},
		normalizeShortcut(shortcut) {
			return {
				business_shortcut_id: shortcut.business_shortcut_id,
				shortcut_value: Number(shortcut.shortcut_value),
				order_number: Number(shortcut.order_number),
				active: false,
				collapsedDelayed: true
			}
		},
		saveShortcutsChanges(shortcuts, callback = null) {
			let data = JSON.stringify(shortcuts.map(shortcut => {
				return {
					shortcut_value: shortcut.shortcut_value,
					order_number: shortcut.order_number
				}
			}));

			API.init().updateBusinessShortcuts(useUserStore().currBusinessId, data, (res) => {
				if(res && res.result && res.result === "OK") {
					this.shortcuts = shortcuts;
					callback && callback(true);
				}else if(res && res.result && res.result === "KO" && res.details === "too_many_shortcuts"){
					callback && callback(res.details);
				}else {
					callback && callback(false);
				}
			})
		},
		externalPaymentIntentEnd(status) {
			if (isAndroid() && this.externalPaymentIntentData) {
				getNative().triggerIntentBack(status);
				this.externalPaymentIntentData = null;
			}
		},
		initBroadcast() {
			if (_broadcastChannel) return;
			_broadcastChannel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
			_broadcastChannel.onmessage = (event) => {
				if (event.data && event.data._type === 'request_state') {
					this.forceBroadcast();
				}
			};
			this.$subscribe(() => {
				if (_broadcastDebounce) return;
				_broadcastDebounce = setTimeout(() => {
					_broadcastDebounce = null;
					this.forceBroadcast();
				}, 30);
			}, { detached: true });
		},
		forceBroadcast(explicit = false) {
			if (!_broadcastChannel) return;
			let payload = {};
			for (const key of _displayStateKeys) {
				payload[key] = this[key];
			}
			if (explicit) payload._explicit = true;
			try {
				_broadcastChannel.postMessage(JSON.parse(JSON.stringify(payload)));
			} catch (e) {
				// Silently ignore serialization errors
			}
		},
		broadcastPaymentComplete(total) {
			if (!_broadcastChannel) return;
			try {
				_broadcastChannel.postMessage({_type: 'payment_complete', total: total});
			} catch (e) {}
		},
		broadcastPaymentStatus(status, amount) {
			if (!_broadcastChannel) return;
			try {
				_broadcastChannel.postMessage({_type: 'payment_status', status: status, amount: amount});
			} catch (e) {}
		},
	},
});
